import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  AuthSignInRequest,
  AuthToken,
  AuthUpdatePasswordRequest,
  AuthSetPasswordRequest,
  AuthMeResponse,
  Card,
  CardCreate,
  CardUpdate,
  CardAIEnrichRequest,
  CardListResponse,
  Category,
  CategoryListResponse,
  CategoryUpdate,
  CategoryWithCardsResponse,
  ReviewedCardsResponse,
  EvaluateMeaningRequest,
  EvaluateMeaningResponse,
  EvaluateExamplePhraseRequest,
  EvaluateExamplePhraseResponse,
  EvaluateSynonymRequest,
  EvaluateSynonymResponse,
  EvaluateAntonymRequest,
  EvaluateAntonymResponse,
  CardReviewRequest,
  CardReviewResponse,
  InstanceListResponse,
  InstanceWithRoleResponse,
  CardReviewsResponse,
  WorkplaceListResponse,
  Workplace,
  Instance,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  UserDetail,
  AddInstanceMemberRequest,
  InstanceMemberResponse,
  InstanceMembersListResponse,
  CreateAssignmentRequest,
  Assignment,
  AssignmentListResponse,
  AssignmentProgressResponse,
  ReviewStatsResponse,
  UnreviewedCardsResponse,
  RecommendedCardsResponse,
} from '../types/api';

export interface SearchCardResult {
  id: number;
  name: string;
  parent_id: number | null;
  category_id: number | null;
  is_folder: boolean;
  instance_id: number;
}

export interface SavedStory {
  id: number;
  category_id: number;
  user_created: number;
  title: string;
  story: string;
  prompt: string | null;
  words_used: { word: string; card_id: number | null }[];
  created_at: string;
}

const RAILWAY_API_BASE = 'https://web-production-89a2.up.railway.app';
const FRONTEND_ORIGIN = 'https://vocabranch.vercel.app';

/** Domains where the app is deployed (proxy is available at /api). */
const DEPLOYED_DOMAINS = ['vocabranch.vercel.app', 'vocabranch.tassiobatista.com', 'app.vocabranch.com'];

/**
 * Returns true if we're in the browser on a deployed domain (where /api proxy exists).
 */
function isDeployedDomain(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const host = window.location.hostname.toLowerCase();
    return DEPLOYED_DOMAINS.some((d) => host === d || host.endsWith('.' + d));
  } catch {
    return false;
  }
}

/**
 * Normalize API base URL: trim and remove trailing slashes.
 * Prevents double slashes when concatenating with paths like '/auth/refresh'.
 * REACT_APP_API_URL must be the backend root (e.g. Railway), not the frontend (e.g. Vercel) URL.
 */
function normalizeApiBaseURL(url: string): string {
  return (url || '').trim().replace(/\/+$/, '');
}

/**
 * Returns true if the given URL is the frontend origin (or a path under it).
 * Used to avoid ever sending API requests to the frontend host root (which returns 405).
 */
function isFrontendOrigin(url: string): boolean {
  const normalized = normalizeApiBaseURL(url).toLowerCase();
  return (
    normalized === FRONTEND_ORIGIN ||
    normalized.startsWith(FRONTEND_ORIGIN + '/')
  );
}

/**
 * Returns true if the given URL is the API proxy path (vocabranch.vercel.app/api).
 * The proxy forwards /api/* to Railway; this path is valid and should NOT be overridden.
 */
function isProxyPath(url: string): boolean {
  const normalized = normalizeApiBaseURL(url).toLowerCase();
  const proxyBase = FRONTEND_ORIGIN + '/api';
  return normalized === proxyBase || normalized.startsWith(proxyBase + '/');
}

class ApiClient {
  private client: AxiosInstance;
  /** API base URL without trailing slash (backend root for all API calls). */
  private baseURL: string;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];
  private onAuthFailure?: () => void;
  private prefetchCache = new Map<string, Promise<CategoryWithCardsResponse[]>>();

  setOnAuthFailure(callback: () => void) {
    this.onAuthFailure = callback;
  }

  constructor(baseURL: string = process.env.REACT_APP_API_URL || RAILWAY_API_BASE) {
    let resolved = (baseURL || process.env.REACT_APP_API_URL || RAILWAY_API_BASE).trim();
    const deprecatedHosts = ['https://vocatree-production-17ae.up.railway.app', 'https://vocatree-production-17ae.up.railway.app/'];
    if (resolved && deprecatedHosts.includes(resolved)) {
      console.warn('Detected deprecated REACT_APP_API_URL value, overriding to new production host.');
      resolved = RAILWAY_API_BASE;
    }
    // On deployed domains: use /api proxy (same-origin) so cookies work. Avoids cross-site cookie blocking.
    if (typeof window !== 'undefined' && isDeployedDomain()) {
      const isDirectRailway = !resolved || resolved === RAILWAY_API_BASE || resolved.startsWith(RAILWAY_API_BASE + '/');
      const isRelativeProxy = resolved === '/api' || resolved.startsWith('/api/');
      if (isDirectRailway && !isRelativeProxy) {
        resolved = '/api';
      }
    }
    // Never use the frontend root as API base (causes 405). Allow the proxy path (vocabranch.vercel.app/api or /api).
    if (typeof window !== 'undefined' && isFrontendOrigin(resolved) && !isProxyPath(resolved)) {
      console.warn('API base URL pointed at frontend origin (non-proxy path); using Railway backend instead.');
      resolved = RAILWAY_API_BASE;
    }
    const normalized = normalizeApiBaseURL(resolved);
    this.baseURL = normalized || RAILWAY_API_BASE;
    if (typeof window !== 'undefined') {
      console.log('ApiClient baseURL resolved:', {
        envValue: process.env.REACT_APP_API_URL,
        finalBaseURL: this.baseURL,
      });
    }
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
      withCredentials: true, // REQUIRED: Send cookies with all requests (cross-origin)
      xsrfCookieName: undefined, // Disable CSRF token handling - not needed for our use case
      xsrfHeaderName: undefined,
    });

    // Request interceptor - ensure withCredentials is always true
    this.client.interceptors.request.use(
      (config) => {
        // CRITICAL: Ensure withCredentials is always set to true for cookie-based auth
        // This is required for cross-origin cookie support
        config.withCredentials = true;
        
        // Log if withCredentials somehow got disabled
        if (!config.withCredentials) {
          console.error('⚠️ WARNING: withCredentials was false! Forcing to true.');
        }
        
        // Log auth-related requests for debugging
        if (config.url?.includes('/auth/')) {
          console.log('🔐 Auth request:', {
            url: `${this.baseURL}${config.url}`,
            method: config.method?.toUpperCase(),
            withCredentials: config.withCredentials,
            headers: Object.keys(config.headers || {}),
            // Note: HttpOnly cookies won't appear in document.cookie, but should be sent automatically
            note: 'HttpOnly cookies are sent automatically if withCredentials is true',
          });
        }
        
        // Log request details for debugging
        if (config.method?.toUpperCase() === 'POST' && config.url?.includes('/cards/')) {
          console.log('POST /cards/ request:', {
            url: `${this.baseURL}${config.url}`,
            method: config.method,
            headers: config.headers,
            data: config.data,
            withCredentials: config.withCredentials,
          });
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for 401 handling with refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        const isAuthEndpoint = originalRequest.url?.includes('/auth/');

        // Handle 401 Unauthorized (but skip refresh for auth endpoints to avoid loops)
        // Also skip if the request is to /auth/signin - let it fail normally with the error message
        const isSignInEndpoint = originalRequest.url?.includes('/auth/signin');
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint && !isSignInEndpoint) {
          // If we're already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Attempt to refresh the token using fetch to ensure credentials are included
            // Use fetch directly for refresh to avoid axios interceptor loops
            const refreshResponse = await fetch(`${this.baseURL}/auth/refresh`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
            });
            
            if (!refreshResponse.ok) {
              throw new Error(`Refresh failed with status ${refreshResponse.status}`);
            }
            
            // Process queued requests
            this.failedQueue.forEach((prom) => prom.resolve());
            this.failedQueue = [];
            
            // Retry the original request with withCredentials ensured
            originalRequest.withCredentials = true;
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed - clear queue and reject
            this.failedQueue.forEach((prom) => prom.reject(refreshError));
            this.failedQueue = [];
            
            // Notify auth context about auth failure
            if (this.onAuthFailure) {
              this.onAuthFailure();
            }
            
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Handle network vs CORS errors so offline issues don't show generic CORS message
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          const isBrowser = typeof window !== 'undefined';
          const isOffline = isBrowser && typeof navigator !== 'undefined' && navigator.onLine === false;
          const looksLikeCors =
            !isOffline &&
            !error.response &&
            error.request &&
            (error.request.status === 0 || error.request.readyState === 4);

          if (isOffline) {
            const offlineError = new Error(
              'Network Error: Unable to reach the API. Please check your internet connection or whether the server is down.'
            );
            offlineError.name = 'NetworkOfflineError';
            return Promise.reject(offlineError);
          }

          if (looksLikeCors) {
            const frontendOrigin = isBrowser ? window.location.origin : 'unknown';
            const corsError = new Error(
              'CORS Error: The API server is not allowing requests from this origin. ' +
              `Please ensure the backend CORS configuration includes: ${frontendOrigin}`
            );
            corsError.name = 'CORSError';
            console.error('CORS Error Details:', {
              frontendOrigin,
              apiUrl: this.baseURL,
              error: error.message
            });
            return Promise.reject(corsError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async signIn(credentials: AuthSignInRequest): Promise<any> {
    // Log the request for debugging
    console.log('Signing in with credentials:', {
      email: credentials.email,
      passwordLength: credentials.password?.length,
      emailTrimmed: credentials.email?.trim(),
      passwordTrimmed: credentials.password?.trim()?.length,
    });
    
    // Trim email and password to avoid whitespace issues
    const trimmedCredentials: AuthSignInRequest = {
      email: credentials.email.trim(),
      password: credentials.password.trim(),
    };
    
    try {
      // Cookies are set by the backend automatically
      console.log('Making signin request...', {
        url: `${this.baseURL}/auth/signin`,
        method: 'POST',
        withCredentials: true,
        data: {
          email: trimmedCredentials.email,
          passwordLength: trimmedCredentials.password.length,
        },
      });
      
      const response = await this.client.post('/auth/signin', trimmedCredentials);
      
      // Check for Set-Cookie headers (axios may not expose these directly)
      const setCookieHeaders = response.headers['set-cookie'];
      const hasSetCookieHeaders = !!setCookieHeaders && (Array.isArray(setCookieHeaders) ? setCookieHeaders.length > 0 : true);
      
      console.log('✅ Sign in response received:', {
        status: response.status,
        statusText: response.statusText,
        hasSetCookieHeaders: hasSetCookieHeaders,
        contentType: response.headers['content-type'],
      });
      
      // Check if Set-Cookie headers are present (note: axios may not expose Set-Cookie in headers)
      // Check browser DevTools Network tab → Response Headers to verify Set-Cookie headers
      console.log('📋 Next step: Check Network tab → /auth/signin → Response Headers → Set-Cookie');
      console.log('📋 Then check: Next request → Request Headers → Cookie: header should contain tokens');
      
      // Return response data in case it contains user info
      return response.data;
    } catch (error: any) {
      console.error('❌ Sign in error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          withCredentials: error.config?.withCredentials,
          data: {
            email: error.config?.data?.email,
            passwordLength: error.config?.data?.password?.length,
          },
          headers: error.config?.headers,
        },
        request: {
          responseURL: error.request?.responseURL,
          status: error.request?.status,
        },
      });
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await this.client.post('/auth/forgot-password', { email });
  }

  async requestMagicCode(data: { email: string; first_name: string; last_name: string }): Promise<{ message: string; is_new_user: boolean }> {
    const response = await this.client.post('/auth/magic-code', data);
    return response.data;
  }

  async verifyMagicCode(data: { email: string; code: string }): Promise<void> {
    await this.client.post('/auth/magic-code/verify', data);
  }

  async signup(data: { first_name: string; email: string; password: string }): Promise<void> {
    await this.client.post('/auth/signup', {
      first_name: data.first_name.trim(),
      email: data.email.trim(),
      password: data.password,
      waitlist: false,
    });
  }

  async refresh(): Promise<void> {
    // Use fetch directly to ensure credentials are always included
    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Refresh failed with status ${response.status}`);
    }
  }

  async getMe(): Promise<AuthMeResponse> {
    try {
      console.log('🔍 Getting user info from /auth/me...');
      const response: AxiosResponse<AuthMeResponse> = await this.client.get('/auth/me');
      console.log('✅ /auth/me successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ /auth/me failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      console.error('⚠️ Check Network tab → /auth/me request → Request Headers → Cookie: header');
      console.error('⚠️ If Cookie: header is missing, cookies are not being sent');
      throw error;
    }
  }

  async debugCookies(): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.client.get('/auth/debug-cookies');
      return response.data;
    } catch (error: any) {
      console.error('Debug cookies error:', error);
      return { error: error.response?.data || error.message };
    }
  }

  async updatePassword(passwordData: AuthUpdatePasswordRequest): Promise<void> {
    await this.client.put('/auth/update-password', passwordData);
  }

  async setPassword(data: AuthSetPasswordRequest): Promise<void> {
    await this.client.post('/auth/set-password', data);
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } catch (error: any) {
      // Even if logout fails, we should clear local state
      console.warn('Logout endpoint failed:', error.message);
    }
  }

  // Cards
  async createCard(card: CardCreate): Promise<Card> {
    try {
      const response: AxiosResponse<Card> = await this.client.post('/cards/', card);
      return response.data;
    } catch (error: any) {
      // Log detailed error information for debugging
      console.error('createCard error details:', {
        url: `${this.baseURL}/cards/`,
        method: 'POST',
        data: card,
        errorCode: error.code,
        errorMessage: error.message,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
        requestHeaders: error.config?.headers,
      });
      throw error;
    }
  }

  async searchCards(params: {
    q: string;
    instance_id: number;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; limit: number; offset: number; items: SearchCardResult[] }> {
    const response = await this.client.get('/cards/search', { params });
    return response.data;
  }

  async getCards(params?: {
    parent_id?: number | null;
    instance_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<CardListResponse> {
    const response: AxiosResponse<CardListResponse> = await this.client.get('/cards/', { params });
    return response.data;
  }

  async getRecentCards(params?: {
    days?: number;
    instance_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<CardListResponse> {
    const response: AxiosResponse<CardListResponse> = await this.client.get('/cards/recent', { params });
    return response.data;
  }

  async getCard(id: number): Promise<Card> {
    const response: AxiosResponse<Card> = await this.client.get(`/cards/${id}`);
    return response.data;
  }

  async updateCard(id: number, card: CardUpdate): Promise<Card> {
    const response: AxiosResponse<Card> = await this.client.patch(`/cards/${id}`, card);
    return response.data;
  }

  async aiEnrichCard(id: number, data: CardAIEnrichRequest): Promise<Card> {
    const response: AxiosResponse<Card> = await this.client.patch(`/cards/${id}/ai-enrich`, data);
    return response.data;
  }

  async aiEnrichBulk(cardIds: number[], language: string): Promise<{ enriched: number; failed: number }> {
    const response: AxiosResponse<{ enriched: number; failed: number }> = await this.client.post('/cards/ai-enrich-bulk', { card_ids: cardIds, language });
    return response.data;
  }

  async createAICard(data: {
    name: string;
    language: string;
    prompt?: string;
    category_id?: number;
    parent_id?: number;
  }): Promise<Card> {
    const response: AxiosResponse<Card> = await this.client.post('/cards/ai-card', data);
    return response.data;
  }

  async deleteCard(id: number): Promise<void> {
    const callId = Math.random().toString(36).substr(2, 9);
    console.log(`[${callId}] API: DELETE /cards/${id}`);
    try {
      await this.client.delete(`/cards/${id}`);
      console.log(`[${callId}] API: Successfully deleted card ${id}`);
    } catch (error: any) {
      console.error(`[${callId}] API: Failed to delete card ${id}:`, error.response?.data || error.message);
      throw error;
    }
  }

  async deleteCardBulk(id: number): Promise<void> {
    const callId = Math.random().toString(36).substr(2, 9);
    console.log(`[${callId}] API: DELETE /cards/${id}/bulk`);
    try {
      await this.client.delete(`/cards/${id}/bulk`);
      console.log(`[${callId}] API: Successfully bulk deleted card ${id}`);
    } catch (error: any) {
      console.error(`[${callId}] API: Failed to bulk delete card ${id}:`, error.response?.data || error.message);
      throw error;
    }
  }

  async moveCard(id: number, data: { parent_id?: number | null; category_id?: number | null }): Promise<void> {
    // Build request body - only include defined fields
    const requestBody: { parent_id?: number | null; category_id?: number | null } = {};
    
    if (data.parent_id !== undefined) {
      requestBody.parent_id = data.parent_id;
    }
    
    if (data.category_id !== undefined) {
      requestBody.category_id = data.category_id;
    }
    
    console.log(`Moving card ${id} with data:`, requestBody);
    await this.client.patch(`/cards/${id}/move`, requestBody);
  }

  async getCardsByParent(parentId: number, params?: {
    instance_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<CardListResponse> {
    const response: AxiosResponse<CardListResponse> = await this.client.get(
      `/cards/by-parent/${parentId}`,
      { params }
    );
    return response.data;
  }

  async getCardsHierarchy(categoryId?: number, instanceId?: number): Promise<Card[]> {
    const params: { category_id?: number; instance_id?: number } = {};
    if (categoryId !== undefined) {
      params.category_id = categoryId;
    }
    if (instanceId !== undefined) {
      params.instance_id = instanceId;
    }
    const response: AxiosResponse<Card[]> = await this.client.get('/cards/hierarchy', { params });
    return response.data;
  }

  async getCardHierarchy(cardId: number): Promise<Card> {
    const response: AxiosResponse<Card> = await this.client.get(`/cards/${cardId}/hierarchy`);
    return response.data;
  }

  async getReviewedCards(options: { days?: number; sort_by?: 'last_reviewed_at' | 'review_count'; instance_id?: number; limit?: number } = {}): Promise<ReviewedCardsResponse> {
    const response: AxiosResponse<ReviewedCardsResponse> = await this.client.get('/cards/reviewed', {
      params: Object.fromEntries(Object.entries(options).filter(([, v]) => v !== undefined)),
    });
    return response.data;
  }

  async getCardReview(cardId: number): Promise<CardReviewResponse | null> {
    try {
      const response: AxiosResponse<CardReviewResponse> = await this.client.get(`/cards/${cardId}/review`);
      return response.data;
    } catch (error: any) {
      // If 404, return null (no review record exists yet)
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async logCardReview(cardId: number, reviewData: CardReviewRequest): Promise<CardReviewResponse> {
    const response: AxiosResponse<CardReviewResponse> = await this.client.post(`/cards/${cardId}/review`, reviewData);
    return response.data;
  }

  async getInstances(): Promise<InstanceListResponse> {
    const response: AxiosResponse<InstanceListResponse> = await this.client.get('/instances/');
    return response.data;
  }

  async getInstance(id: number): Promise<InstanceWithRoleResponse> {
    const response: AxiosResponse<InstanceWithRoleResponse> = await this.client.get(`/instances/${id}`);
    return response.data;
  }

  async getUser(id: number): Promise<UserDetail> {
    const response: AxiosResponse<UserDetail> = await this.client.get(`/users/${id}`);
    return response.data;
  }

  async getCardReviews(instanceId?: number, days?: number): Promise<CardReviewsResponse> {
    const params: { instance_id?: number; days?: number } = {};
    if (instanceId !== undefined) {
      params.instance_id = instanceId;
    }
    if (days !== undefined) {
      params.days = days;
    }
    const response: AxiosResponse<CardReviewsResponse> = await this.client.get('/card-reviews', { params });
    return response.data;
  }

  async getAllCardReviews(instanceId?: number, days?: number): Promise<CardReviewsResponse> {
    const params: { instance_id?: number; days?: number; fetch_all?: boolean } = { fetch_all: true };
    if (instanceId !== undefined) {
      params.instance_id = instanceId;
    }
    if (days !== undefined) {
      params.days = days;
    }
    const response: AxiosResponse<CardReviewsResponse> = await this.client.get('/card-reviews', { params });
    return response.data;
  }

  async getWorkplaces(): Promise<WorkplaceListResponse> {
    const response: AxiosResponse<WorkplaceListResponse> = await this.client.get('/workplaces/');
    return response.data;
  }

  async createWorkplace(data: { name: string; description?: string }): Promise<Workplace> {
    const response: AxiosResponse<Workplace> = await this.client.post('/workplaces/', data);
    return response.data;
  }

  async createCollection(data: CreateCollectionRequest): Promise<Instance> {
    const response: AxiosResponse<Instance> = await this.client.post('/instances/', data);
    return response.data;
  }

  async updateCollection(id: number, data: UpdateCollectionRequest): Promise<Instance> {
    const response: AxiosResponse<Instance> = await this.client.patch(`/instances/${id}`, data);
    return response.data;
  }

  async getCollectionDeletionImpact(id: number): Promise<{ instance_id: number; categories_to_delete: number; cards_to_delete: number }> {
    const response = await this.client.get(`/instances/${id}/deletion-impact`);
    return response.data;
  }

  async deleteCollection(id: number): Promise<void> {
    await this.client.delete(`/instances/${id}`);
  }

  async addInstanceMember(instanceId: number, email: string): Promise<InstanceMemberResponse> {
    const body: AddInstanceMemberRequest = { email, role: 'viewer' };
    try {
      const response: AxiosResponse<InstanceMemberResponse> = await this.client.post(
        `/instances/${instanceId}/members`,
        body
      );
      return response.data;
    } catch (err: any) {
      // If server returned 2xx but something threw (e.g. response body parsing), treat as success
      if (err.response?.status >= 200 && err.response?.status < 300) {
        return (err.response?.data ?? {}) as InstanceMemberResponse;
      }
      throw err;
    }
  }

  async getInstanceMembers(instanceId: number): Promise<InstanceMembersListResponse> {
    const response: AxiosResponse<InstanceMembersListResponse> = await this.client.get(
      `/instances/${instanceId}/members`,
      { params: { role: 'viewer', status: 'active' } }
    );
    return response.data;
  }

  async removeInstanceMember(instanceId: number, userId: number): Promise<void> {
    await this.client.delete(`/instances/${instanceId}/members/${userId}`);
  }

  // Categories
  async getCategories(instanceId?: number): Promise<Category[]> {
    const params = instanceId !== undefined ? { instance_id: instanceId } : {};
    const response: AxiosResponse<CategoryListResponse> = await this.client.get('/categories/', { params });
    return response.data.items;
  }

  prefetchCategoriesWithCards(instanceId?: number): void {
    const key = String(instanceId ?? 'none');
    if (!this.prefetchCache.has(key)) {
      const params = instanceId !== undefined ? { instance_id: instanceId } : {};
      this.prefetchCache.set(key, this.client.get<CategoryWithCardsResponse[]>('/categories/with-cards', { params }).then(r => r.data));
    }
  }

  async getCategoriesWithCards(instanceId?: number): Promise<CategoryWithCardsResponse[]> {
    const key = String(instanceId ?? 'none');
    const prefetched = this.prefetchCache.get(key);
    this.prefetchCache.delete(key);
    if (prefetched) return prefetched;
    const params = instanceId !== undefined ? { instance_id: instanceId } : {};
    const response: AxiosResponse<CategoryWithCardsResponse[]> = await this.client.get('/categories/with-cards', { params });
    return response.data;
  }

  async createCategory(name: string, instanceId: number, isDefault: boolean = false): Promise<Category> {
    const response: AxiosResponse<Category> = await this.client.post('/categories/', { 
      name, 
      instance_id: instanceId,
      is_default: isDefault 
    });
    return response.data;
  }

  async updateCategory(id: number, category: CategoryUpdate): Promise<Category> {
    const response: AxiosResponse<Category> = await this.client.patch(`/categories/${id}`, category);
    return response.data;
  }

  async getAdminUsers(): Promise<{ total: number; items: { id: number; email: string; first_name: string | null; last_name: string | null; status: string; has_password: boolean }[] }> {
    const response = await this.client.get('/admin/users');
    return response.data;
  }

  async approveUser(id: number): Promise<void> {
    await this.client.patch(`/admin/users/${id}`, { status: 'approved' });
  }

  async generateAITree(data: {
    language: string;
    instance_id: number;
    prompt: string;
    category_name?: string;
    cards_only?: boolean;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.client.post('/categories/ai-generate-tree', data);
    return response.data;
  }

  async generateFromWordList(data: {
    words: string[];
    language: string;
    instance_id: number;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.client.post('/categories/from-word-list', data);
    return response.data;
  }

  async generateFromText(data: {
    text: string;
    word_type: string;
    language: string;
    instance_id: number;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.client.post('/categories/from-text', data);
    return response.data;
  }

  async generateAIFolder(data: {
    category_id: number;
    prompt: string;
    language: string;
    parent_id: number | null;
    folder_name?: string;
    cards_only?: boolean;
  }): Promise<any> {
    const response: AxiosResponse<any> = await this.client.post('/cards/ai-generate-folder', data);
    return response.data;
  }

  async bulkDeleteCategory(id: number): Promise<void> {
    console.log(`API: POST /categories/bulk-delete with category_id: ${id}`);
    try {
      await this.client.post(`/categories/bulk-delete`, { category_id: id });
      console.log(`API: Successfully bulk deleted category ${id}`);
    } catch (error: any) {
      console.error(`API: Failed to bulk delete category ${id}:`, error.response?.data || error.message);
      throw error;
    }
  }

  async evaluateMeaning(data: EvaluateMeaningRequest): Promise<EvaluateMeaningResponse> {
    const response: AxiosResponse<EvaluateMeaningResponse> = await this.client.post('/cards/evaluate-meaning', data);
    return response.data;
  }

  async evaluateExamplePhrase(data: EvaluateExamplePhraseRequest): Promise<EvaluateExamplePhraseResponse> {
    const response: AxiosResponse<EvaluateExamplePhraseResponse> = await this.client.post('/cards/evaluate-example-phrase', data);
    return response.data;
  }

  async evaluateSynonym(data: EvaluateSynonymRequest): Promise<EvaluateSynonymResponse> {
    const response: AxiosResponse<EvaluateSynonymResponse> = await this.client.post('/cards/evaluate-synonym', data);
    return response.data;
  }

  async evaluateAntonym(data: EvaluateAntonymRequest): Promise<EvaluateAntonymResponse> {
    const response: AxiosResponse<EvaluateAntonymResponse> = await this.client.post('/cards/evaluate-antonym', data);
    return response.data;
  }

  // Feedback
  async sendFeedback(data: { subject: string; message: string }): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.client.post('/feedback/', data);
    return response.data;
  }

  // Assignments
  async createAssignment(data: CreateAssignmentRequest): Promise<Assignment> {
    const response: AxiosResponse<Assignment> = await this.client.post('/assignments/', data);
    return response.data;
  }

  async getAssignments(): Promise<AssignmentListResponse> {
    const response: AxiosResponse<AssignmentListResponse> = await this.client.get('/assignments/');
    return response.data;
  }

  async deleteAssignment(id: number): Promise<void> {
    await this.client.delete(`/assignments/${id}/`);
  }

  async getAssignmentProgress(id: number): Promise<AssignmentProgressResponse> {
    const response: AxiosResponse<AssignmentProgressResponse> = await this.client.get(`/assignments/${id}/progress`);
    return response.data;
  }

  async getUnreviewedCards(instanceId?: number, limit = 50, offset = 0): Promise<UnreviewedCardsResponse> {
    const response: AxiosResponse<UnreviewedCardsResponse> = await this.client.get('/cards/unreviewed', {
      params: { instance_id: instanceId, limit, offset },
    });
    return response.data;
  }

  async getRecommendedCards(instanceId?: number): Promise<RecommendedCardsResponse> {
    const response: AxiosResponse<RecommendedCardsResponse> = await this.client.get('/cards/recommended', {
      params: instanceId ? { instance_id: instanceId } : undefined,
    });
    return response.data;
  }

  async getReviewStats(): Promise<ReviewStatsResponse> {
    const utcOffsetMinutes = new Date().getTimezoneOffset() * -1;
    const response: AxiosResponse<ReviewStatsResponse> = await this.client.get('/users/me/review-stats', {
      params: { utc_offset_minutes: utcOffsetMinutes },
    });
    return response.data;
  }

  async generatePhrases(cardId: number, data: { prompt?: string; language?: string }): Promise<{ phrases: string[] }> {
    const response: AxiosResponse<{ phrases: string[] }> = await this.client.post(`/cards/${cardId}/generate-phrases`, data);
    return response.data;
  }

  getCardPdfUrl(cardId: number): string {
    return `${this.baseURL}/cards/${cardId}/pdf`;
  }

  async generateStory(categoryId: number, data: { prompt?: string }): Promise<{ story: string; words_used: string[]; total_words_in_category: number }> {
    const response = await this.client.post(`/categories/${categoryId}/generate-story`, data);
    return response.data;
  }

  async generateTextFromCards(data: { card_ids: number[]; prompt?: string }): Promise<{ text: string; words_used: string[]; total_words_requested: number }> {
    const response = await this.client.post('/cards/generate-text', data);
    return response.data;
  }

  async generateCollocations(cardId: number, data: { prompt?: string }): Promise<{ word: string; items: { collocation: string; example: string }[] }> {
    const response = await this.client.post(`/cards/${cardId}/generate-collocations`, data);
    return response.data;
  }

  async addCollocation(cardId: number, collocation: string): Promise<{ collocations: string[] }> {
    const response = await this.client.post(`/cards/${cardId}/collocations`, { collocation });
    return response.data;
  }

  async generateSynonyms(cardId: number, data: { prompt?: string }): Promise<{ word: string; items: { synonym: string; example: string }[] }> {
    const response = await this.client.post(`/cards/${cardId}/generate-synonyms`, data);
    return response.data;
  }

  async addSynonym(cardId: number, synonym: string): Promise<{ synonyms: string[] }> {
    const response = await this.client.post(`/cards/${cardId}/synonyms`, { synonym });
    return response.data;
  }

  async generateAntonyms(cardId: number, data: { prompt?: string }): Promise<{ word: string; items: { antonym: string; example: string }[] }> {
    const response = await this.client.post(`/cards/${cardId}/generate-antonyms`, data);
    return response.data;
  }

  async addAntonym(cardId: number, antonym: string): Promise<{ antonyms: string[] }> {
    const response = await this.client.post(`/cards/${cardId}/antonyms`, { antonym });
    return response.data;
  }

  async generateImage(cardId: number, data: { prompt?: string }): Promise<{ image_b64: string }> {
    const body: Record<string, string> = {};
    if (data.prompt) body.prompt = data.prompt;
    const response = await this.client.post(`/cards/${cardId}/generate-image`, body);
    return response.data;
  }

  async saveImage(cardId: number, image_b64: string): Promise<{ images: string[] }> {
    const response = await this.client.post(`/cards/${cardId}/save-image`, { image_b64 });
    return response.data;
  }

  async deleteImage(cardId: number, image_url: string): Promise<{ images: string[] }> {
    const response = await this.client.delete(`/cards/${cardId}/images`, { data: { image_url } });
    return response.data;
  }

  async tts(text: string, lang?: string): Promise<string> {
    const response = await this.client.post('/audio/tts', { text, lang }, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  }

  async saveStory(categoryId: number, data: { story: string; words_used: string[]; prompt?: string }): Promise<SavedStory> {
    const response = await this.client.post(`/categories/${categoryId}/stories`, data);
    return response.data;
  }

  async listStories(categoryId: number): Promise<{ total: number; items: SavedStory[] }> {
    const response = await this.client.get(`/categories/${categoryId}/stories`);
    return response.data;
  }

  async updateStory(categoryId: number, storyId: number, data: { title?: string; story?: string; words_used?: { word: string; card_id: number | null }[] }): Promise<SavedStory> {
    const response = await this.client.patch(`/categories/${categoryId}/stories/${storyId}`, data);
    return response.data;
  }

  async deleteStory(categoryId: number, storyId: number): Promise<void> {
    await this.client.delete(`/categories/${categoryId}/stories/${storyId}`);
  }

  async generateFolderStory(folderId: number, data: { prompt?: string }): Promise<{ story: string; words_used: string[]; total_words_in_folder: number }> {
    const response = await this.client.post(`/cards/${folderId}/generate-story`, data);
    return response.data;
  }

  async saveFolderStory(folderId: number, data: { story: string; words_used: string[]; prompt?: string }): Promise<SavedStory> {
    const response = await this.client.post(`/cards/${folderId}/stories`, data);
    return response.data;
  }

  async listFolderStories(folderId: number): Promise<{ total: number; items: SavedStory[] }> {
    const response = await this.client.get(`/cards/${folderId}/stories`);
    return response.data;
  }

  async updateFolderStory(folderId: number, storyId: number, data: { title?: string; story?: string; words_used?: { word: string; card_id: number | null }[] }): Promise<SavedStory> {
    const response = await this.client.patch(`/cards/${folderId}/stories/${storyId}`, data);
    return response.data;
  }

  async deleteFolderStory(folderId: number, storyId: number): Promise<void> {
    await this.client.delete(`/cards/${folderId}/stories/${storyId}`);
  }

  async reviseStory(storyId: number, data: { prompt?: string; vocabulary_source?: 'free' | 'library' }): Promise<{ revised: string; original: string; vocabulary_source?: string; vocabulary_word_count?: number; vocabulary_words_used?: { word: string; card_id: number }[] }> {
    const response = await this.client.post(`/cards/stories/${storyId}/revise`, data);
    return response.data;
  }

  async transcribeAudio(file: Blob, language = 'en', context?: string): Promise<{ transcript: string }> {
    const formData = new FormData();
    formData.append('file', file, 'recording.webm');
    formData.append('language', language);
    if (context) formData.append('context', context);
    const response: AxiosResponse<{ transcript: string }> = await this.client.post('/audio/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async createCheckoutSession(): Promise<{ url: string }> {
    const response: AxiosResponse<{ url: string }> = await this.client.post('/subscriptions/checkout');
    return response.data;
  }

  async createPortalSession(): Promise<{ url: string }> {
    const response: AxiosResponse<{ url: string }> = await this.client.post('/subscriptions/portal');
    return response.data;
  }

  async generateContextualQuestion(data: {
    category_id?: number;
    folder_id?: number;
    language: string;
    prompt?: string;
  }): Promise<{ question: string; words_tested: string[] }> {
    const response = await this.client.post('/practice/contextual/question', data);
    return response.data;
  }

  async generateContextualQuestionFromCards(data: {
    card_ids: number[];
    language: string;
    prompt?: string;
  }): Promise<{ question: string; words_tested: string[] }> {
    const response = await this.client.post('/practice/contextual/question-from-cards', data);
    return response.data;
  }

  async evaluateContextualAnswer(data: {
    question: string;
    words_tested: string[];
    user_answer: string;
    language: string;
    prompt?: string;
  }): Promise<{
    grammar: { has_errors: boolean; errors: string | null };
    naturalness: string;
    naturalness_reason: string | null;
    native_speaker_alternative: string;
    feedback: string;
  }> {
    const response = await this.client.post('/practice/contextual/evaluate', data);
    return response.data;
  }

  async proposeArrangement(categoryId: number, prompt?: string): Promise<import('../types/api').ArrangeProposeResponse> {
    const response = await this.client.post(`/categories/${categoryId}/arrange/propose`, { prompt });
    return response.data;
  }

  async applyArrangement(categoryId: number, groups: import('../types/api').ArrangementGroup[]): Promise<import('../types/api').ArrangeApplyResponse> {
    const response = await this.client.post(`/categories/${categoryId}/arrange/apply`, { groups });
    return response.data;
  }

  async listDailyReviewGoals(status?: import('../types/api').GoalStatus): Promise<import('../types/api').DailyReviewGoal[]> {
    const response = await this.client.get('/goals/daily-review', { params: status ? { status } : undefined });
    return response.data.items ?? response.data;
  }

  async createDailyReviewGoal(data: import('../types/api').CreateDailyReviewGoalRequest): Promise<import('../types/api').DailyReviewGoal> {
    const response = await this.client.post('/goals/daily-review', data);
    return response.data;
  }

  async getDailyReviewGoal(id: number): Promise<import('../types/api').DailyReviewGoal> {
    const response = await this.client.get(`/goals/daily-review/${id}`);
    return response.data;
  }

  async updateDailyReviewGoalStatus(id: number, status: import('../types/api').GoalStatus): Promise<import('../types/api').DailyReviewGoal> {
    const response = await this.client.patch(`/goals/daily-review/${id}`, { status });
    return response.data;
  }

  async deleteDailyReviewGoal(id: number): Promise<void> {
    await this.client.delete(`/goals/daily-review/${id}`);
  }

  // No token storage methods needed - using HTTP-only cookies
}

export const apiClient = new ApiClient();
export default apiClient;
